import { lazy, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spin } from 'antd';
import { Navigate, Route, Routes } from 'react-router-dom';
import { api, ApiError } from './api';
import { AppLayout } from './components/AppLayout';
import type { AuthResponse } from './types';

const Dashboard = lazy(() => import('./pages/Dashboard').then((module) => ({ default: module.Dashboard })));
const Login = lazy(() => import('./pages/Login').then((module) => ({ default: module.Login })));
const Predictions = lazy(() => import('./pages/Predictions').then((module) => ({ default: module.Predictions })));
const Subscriptions = lazy(() => import('./pages/Subscriptions').then((module) => ({ default: module.Subscriptions })));
const Deliveries = lazy(() => import('./pages/Operations').then((module) => ({ default: module.Deliveries })));
const Broadcasts = lazy(() => import('./pages/Broadcasts').then((module) => ({ default: module.Broadcasts })));
const Logs = lazy(() => import('./pages/Operations').then((module) => ({ default: module.Logs })));
const Blacklist = lazy(() => import('./pages/Operations').then((module) => ({ default: module.Blacklist })));
const Bots = lazy(() => import('./pages/Configuration').then((module) => ({ default: module.Bots })));
const Buttons = lazy(() => import('./pages/Configuration').then((module) => ({ default: module.Buttons })));
const Media = lazy(() => import('./pages/Configuration').then((module) => ({ default: module.Media })));
const Messages = lazy(() => import('./pages/Configuration').then((module) => ({ default: module.Messages })));
const Settings = lazy(() => import('./pages/Configuration').then((module) => ({ default: module.Settings })));
const Statistics = lazy(() => import('./pages/Configuration').then((module) => ({ default: module.Statistics })));
const Admins = lazy(() => import('./pages/Configuration').then((module) => ({ default: module.Admins })));

function ProtectedApp() {
  const auth = useQuery({ queryKey: ['auth'], queryFn: () => api<AuthResponse>('/api/auth/me'), retry: false });
  if (auth.isLoading) return <div className="center-screen"><Spin size="large" /></div>;
  if (auth.error instanceof ApiError && auth.error.status === 401) return <Navigate to="/login" replace />;
  if (!auth.data) return <Navigate to="/login" replace />;
  return <AppLayout user={auth.data.user} />;
}

export function App() {
  return (
    <Suspense fallback={<div className="center-screen"><Spin size="large" /></div>}>
      <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<ProtectedApp />}>
        <Route index element={<Dashboard />} />
        <Route path="predictions" element={<Predictions />} />
        <Route path="statistics" element={<Statistics />} />
        <Route path="subscriptions" element={<Subscriptions />} />
        <Route path="private-users" element={<Subscriptions chatType="PRIVATE" />} />
        <Route path="groups" element={<Subscriptions chatType="GROUP,SUPERGROUP" />} />
        <Route path="channels" element={<Subscriptions chatType="CHANNEL" />} />
        <Route path="bots" element={<Bots />} />
        <Route path="yu28" element={<Bots yu28Only />} />
        <Route path="media" element={<Media />} />
        <Route path="buttons" element={<Buttons />} />
        <Route path="messages" element={<Messages />} />
        <Route path="deliveries" element={<Deliveries />} />
        <Route path="broadcasts" element={<Broadcasts />} />
        <Route path="failures" element={<Deliveries failedOnly />} />
        <Route path="blacklist" element={<Blacklist />} />
        <Route path="logs" element={<Logs />} />
        <Route path="admins" element={<Admins />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
